import { Vacacion } from "../shared/model/vacaciones/vacacion.model";

/**
 * Días sueltos como UNA solicitud (15/10/2026, decisión de Norman: «un
 * pedido con sus tramos»).
 *
 * El servidor guarda cada tramo de días seguidos en su renglón —así el
 * saldo, la asistencia y los calendarios cuentan igual que siempre— y los
 * tramos del mismo pedido llevan el id del primero (`idPedido`). Las listas
 * de solicitudes (Mis vacaciones, Mi equipo, Autorizaciones, Seguimiento)
 * enseñan el pedido como una sola: se autoriza, se rechaza o se retira
 * completo, con un solo FRC-019. Los calendarios siguen pintando tramo por
 * tramo: ahí lo que importa son los días.
 */

/**
 * Junta los renglones de un mismo pedido en uno. El que queda es el del
 * primer tramo (su id es el del pedido: con él se resuelve, se retira y se
 * baja el FRC-019), con el rango de la primera a la última fecha y los
 * tramos en orden. Todo lo demás se queda como venía y en el lugar del
 * primer renglón del pedido que aparece. Si de un pedido solo llegó un
 * tramo, se queda como una solicitud cualquiera.
 */
export function agrupaPedidos(lista: Vacacion[]): Vacacion[] {
  const grupos = new Map<number, Vacacion[]>();
  for (const v of lista) {
    if (v.idPedido != null) {
      const grupo = grupos.get(v.idPedido) ?? [];
      grupo.push(v);
      grupos.set(v.idPedido, grupo);
    }
  }
  const resultado: Vacacion[] = [];
  for (const v of lista) {
    if (v.idPedido == null) {
      resultado.push(v);
      continue;
    }
    const grupo = grupos.get(v.idPedido);
    if (!grupo) {
      // Ya se juntó en un renglón anterior del mismo pedido.
      continue;
    }
    grupos.delete(v.idPedido);
    resultado.push(grupo.length > 1 ? juntaTramos(grupo) : v);
  }
  return resultado;
}

/** Un pedido de varios tramos como una solicitud. */
function juntaTramos(grupo: Vacacion[]): Vacacion {
  const tramos = grupo.slice().sort((a, b) => (a.fechaInicio || "").localeCompare(b.fechaInicio || ""));
  const cabeza =
    grupo.find((t) => t.idVacacion === t.idPedido) ??
    grupo.reduce((a, b) => ((b.idVacacion ?? 0) < (a.idVacacion ?? 0) ? b : a), grupo[0]);
  return { ...cabeza, fechaInicio: tramos[0].fechaInicio, fechaFin: tramos.at(-1)?.fechaFin, tramos };
}

/** Los tramos de una solicitud: los de su pedido, o ella sola. */
export function tramosDe(v: Vacacion): Vacacion[] {
  return v.tramos?.length ? v.tramos : [v];
}
